import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, FileText, Loader2, Plus, RefreshCw, X } from 'lucide-react';
import { apiService, DocumentAssetInfo } from '../services/api';

interface WorkspaceDocumentsSectionProps {
  sessionId: string | null;
}

const SUPPORTED_DOC_EXTENSIONS = ['.pdf', '.docx', '.doc', '.pptx', '.ppt', '.txt', '.md'];

const guessContentType = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const map: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.doc': 'application/msword',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.txt': 'text/plain',
    '.md': 'text/markdown',
  };
  return map[ext] || 'application/octet-stream';
};

/**
 * Compact "Documents" row for the workspace Research Prep section.
 *
 * Shows a collapsed one-line summary with an add (+) button; expanding it
 * reveals the uploaded documents (including any uploaded earlier in Brief
 * Builder, since they share the workspace session). Documents uploaded here
 * are picked up by report generation when the user starts research.
 */
const WorkspaceDocumentsSection: React.FC<WorkspaceDocumentsSectionProps> = ({ sessionId }) => {
  const [documents, setDocuments] = useState<DocumentAssetInfo[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [docUpload, setDocUpload] = useState<{ name: string; percent: number } | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refreshDocuments = useCallback(async () => {
    if (!sessionId) {
      setDocuments([]);
      return;
    }
    try {
      const result = await apiService.listDocuments({ sessionId });
      setDocuments(result.documents || []);
    } catch {
      // Non-fatal: keep the current list.
    }
  }, [sessionId]);

  useEffect(() => {
    void refreshDocuments();
  }, [refreshDocuments]);

  const docsProcessing = documents.some(
    (doc) => doc.status === 'queued' || doc.status === 'parsing'
  );

  useEffect(() => {
    if (!docsProcessing) return undefined;
    const timer = window.setInterval(() => {
      void refreshDocuments();
    }, 2500);
    return () => window.clearInterval(timer);
  }, [docsProcessing, refreshDocuments]);

  const handleDocumentPicked = useCallback(
    async (file: File) => {
      if (!sessionId) return;
      setDocError(null);
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!SUPPORTED_DOC_EXTENSIONS.includes(ext)) {
        setDocError(`Unsupported file type ${ext}. Use PDF, Word, PowerPoint, or text.`);
        return;
      }
      setExpanded(true);
      setDocUpload({ name: file.name, percent: 0 });
      try {
        const contentType = guessContentType(file);
        const initiated = await apiService.initiateAttachment(file.name, contentType, sessionId);
        await apiService.uploadFileToSignedUrl(
          initiated.upload_url,
          file,
          contentType,
          (percent) => setDocUpload({ name: file.name, percent })
        );
        await apiService.completeAttachment(initiated.attachment_id, { sizeBytes: file.size });
        await refreshDocuments();
      } catch (err) {
        setDocError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setDocUpload(null);
      }
    },
    [refreshDocuments, sessionId]
  );

  const handleDocumentRetry = useCallback(
    async (docId: string) => {
      setDocError(null);
      try {
        await apiService.retryDocument(docId);
        await refreshDocuments();
      } catch (err) {
        setDocError(err instanceof Error ? err.message : 'Retry failed');
      }
    },
    [refreshDocuments]
  );

  const handleDocumentDelete = useCallback(async (docId: string) => {
    setDocError(null);
    try {
      await apiService.deleteDocument(docId);
      setDocuments((prev) => prev.filter((doc) => doc.doc_id !== docId));
    } catch (err) {
      setDocError(err instanceof Error ? err.message : 'Delete failed');
    }
  }, []);

  if (!sessionId) return null;

  const docCount = documents.length + (docUpload ? 1 : 0);

  return (
    <div className="workspace-docs">
      <div className="workspace-docs__header">
        <button
          type="button"
          className="workspace-docs__toggle"
          onClick={() => setExpanded((prev) => !prev)}
          title={expanded ? 'Collapse documents' : 'Show documents'}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <FileText size={12} />
          <span className="workspace-docs__label">
            Documents{docCount > 0 ? ` (${docCount})` : ''}
          </span>
          {docsProcessing && <Loader2 size={12} className="brief-spinner" />}
        </button>
        <button
          type="button"
          className="workspace-docs__add"
          onClick={() => fileInputRef.current?.click()}
          disabled={docUpload !== null}
          title="Upload a document (PDF, Word, PowerPoint) for report generation"
        >
          <Plus size={12} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORTED_DOC_EXTENSIONS.join(',')}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleDocumentPicked(file);
            e.target.value = '';
          }}
        />
      </div>
      {expanded && (
        <div className="workspace-docs__list">
          {docUpload && (
            <div className="workspace-docs__item">
              <Loader2 size={12} className="brief-spinner" />
              <span className="workspace-docs__item-name">{docUpload.name}</span>
              <span className="workspace-docs__item-status">{docUpload.percent}%</span>
            </div>
          )}
          {documents.map((doc) => (
            <div key={doc.doc_id} className="workspace-docs__item">
              {doc.status === 'ready' ? (
                <CheckCircle2 size={12} className="workspace-docs__icon-ready" />
              ) : doc.status === 'failed' || doc.status === 'stuck' ? (
                <FileText size={12} className="workspace-docs__icon-failed" />
              ) : (
                <Loader2 size={12} className="brief-spinner" />
              )}
              <span className="workspace-docs__item-name" title={doc.filename}>
                {doc.filename}
              </span>
              {(doc.status === 'queued' || doc.status === 'parsing') && (
                <span className="workspace-docs__item-status">Processing…</span>
              )}
              {(doc.status === 'failed' || doc.status === 'stuck') && (
                <button
                  type="button"
                  className="workspace-docs__item-action"
                  onClick={() => void handleDocumentRetry(doc.doc_id)}
                  title={doc.error || 'Retry processing'}
                >
                  <RefreshCw size={11} />
                </button>
              )}
              <button
                type="button"
                className="workspace-docs__item-action"
                onClick={() => void handleDocumentDelete(doc.doc_id)}
                title="Remove document"
              >
                <X size={11} />
              </button>
            </div>
          ))}
          {documents.length === 0 && !docUpload && (
            <div className="workspace-docs__empty">
              No documents yet. Add PDFs, Word, or PowerPoint files to inform the reports.
            </div>
          )}
          {docError && <div className="workspace-docs__error">{docError}</div>}
        </div>
      )}
    </div>
  );
};

export default WorkspaceDocumentsSection;
